import { X, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { useState } from 'react';
import { CheckType, InvestigationCategory } from '../../types';
import { useValidateInvestigationMutation } from '../../store/api/apiSlice';
import { validateInvestigationFile } from '../../utils/fileValidation';
import { splitFullName } from '../../utils/nameParts';

interface NewBackgroundCheckModalProps {
    isOpen: boolean;
    onClose: () => void;
    onNext: (details: FormData) => void;
    checkType: CheckType;
    externalLoading?: boolean;
    externalError?: string;
}

interface FormData {
    fullName: string;
    employeeEmail: string;
    employeePhone: string;
    employeeGender: string;
    countryCode: string;
    investigationCategory: InvestigationCategory;
    documents?: File[];
    documentTypes?: string[];
}

export default function NewBackgroundCheckModal({
    isOpen,
    onClose,
    onNext,
    checkType,
    externalLoading = false,
    externalError = '',
}: NewBackgroundCheckModalProps) {
    const getReadableError = (err: any, fallback = 'Validation failed') => {
        const serverMessage = err?.data?.message ?? err?.message ?? err;

        if (Array.isArray(serverMessage)) {
            return serverMessage.join(', ');
        }

        if (typeof serverMessage === 'string') {
            return serverMessage;
        }

        if (serverMessage && typeof serverMessage === 'object') {
            return serverMessage.message || serverMessage.error || JSON.stringify(serverMessage);
        }

        return fallback;
    };

    const [formData, setFormData] = useState<FormData>({
        fullName: '',
        employeeEmail: '',
        employeePhone: '',
        employeeGender: '',
        countryCode: '+234', // Default to Nigeria
        investigationCategory: InvestigationCategory.EMPLOYMENT,
        documents: [],
        documentTypes: [],
    });
    const [uploadedFiles, setUploadedFiles] = useState<{ file: File; type: string; id: string }[]>([]);
    const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
    const [isValidating, setIsValidating] = useState(false);

    const [validateInvestigation] = useValidateInvestigationMutation();

    if (!isOpen) return null;

    const handleInputChange = (field: keyof FormData, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        // Clear error for this field when user starts typing
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: undefined }));
        }
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, docType: string) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            // Validate every picked file against the backend rules (10 MB, jpeg/png/pdf/doc/docx).
            const accepted: { file: File; type: string; id: string }[] = [];
            const rejected: string[] = [];
            Array.from(files).forEach((file, i) => {
                const validationError = validateInvestigationFile(file);
                if (validationError) {
                    rejected.push(`${file.name}: ${validationError}`);
                } else {
                    accepted.push({ file, type: docType, id: `${Date.now()}-${i}-${Math.random()}` });
                }
            });

            // Reset the input so re-selecting the same file re-fires onChange.
            e.target.value = '';

            setErrors((prev) => {
                const next = { ...prev };
                if (rejected.length > 0) next.documents = rejected.join(' · ');
                else delete next.documents;
                return next;
            });
            if (accepted.length === 0) return;

            setUploadedFiles((prev) => [...prev, ...accepted]);

            // Update form data
            const allFiles = [...uploadedFiles, ...accepted];
            setFormData((prev) => ({
                ...prev,
                documents: allFiles.map((f) => f.file),
                documentTypes: allFiles.map((f) => f.type),
            }));
        }
    };

    const handleRemoveFile = (fileId: string) => {
        const updatedFiles = uploadedFiles.filter((f) => f.id !== fileId);
        setUploadedFiles(updatedFiles);
        setFormData((prev) => ({
            ...prev,
            documents: updatedFiles.map((f) => f.file),
            documentTypes: updatedFiles.map((f) => f.type),
        }));
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<Record<keyof FormData, string>> = {};

        if (!formData.fullName.trim()) {
            newErrors.fullName = 'Full name is required';
        } else if (!splitFullName(formData.fullName)) {
            // Stored as separate parts, so a surname is not optional.
            newErrors.fullName = 'Enter both a first and last name';
        }
        if (!formData.employeeEmail.trim()) {
            newErrors.employeeEmail = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.employeeEmail)) {
            newErrors.employeeEmail = 'Invalid email format';
        }
        if (!formData.employeePhone.trim()) {
            newErrors.employeePhone = 'Phone number is required';
        } else {
            // Validate phone number format
            const phoneNumber = formData.employeePhone.replace(/\s+/g, '');

            // Remove leading zero for validation
            const cleanPhone = phoneNumber.replace(/^0+/, '');

            // Check if it's all digits
            if (!/^\d+$/.test(cleanPhone)) {
                newErrors.employeePhone = 'Phone number must contain only digits';
            } else if (cleanPhone.length !== 10) {
                newErrors.employeePhone = 'Phone number must be exactly 10 digits';
            }
        }

        // Check document limit
        if (uploadedFiles.length > 10) {
            newErrors.documents = 'Maximum 10 files allowed';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = async () => {
        if (!validateForm()) {
            // Scroll to first error
            const firstError = Object.keys(errors)[0];
            if (firstError) {
                const element = document.querySelector(`[name="${firstError}"]`);
                element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        // Format phone number with country code
        // Remove all spaces and leading zeros
        const cleanPhone = formData.employeePhone.replace(/\s+/g, '').replace(/^0+/, '');

        // Add country code if not already present
        const phoneNumberWithCountryCode = cleanPhone.startsWith('+')
            ? cleanPhone
            : `${formData.countryCode}${cleanPhone}`;

        // Validated above, so this cannot be null by here.
        const nameParts = splitFullName(formData.fullName);

        // Validate with backend before proceeding to payment
        setIsValidating(true);
        try {
            const validationData = {
                checkType,
                employeeFirstName: nameParts?.first ?? '',
                ...(nameParts?.middle ? { employeeMiddleName: nameParts.middle } : {}),
                employeeLastName: nameParts?.last ?? '',
                employeeEmail: formData.employeeEmail.trim(),
                employeePhone: phoneNumberWithCountryCode,
                ...(formData.employeeGender && { employeeGender: formData.employeeGender }),
            };

            const result = await validateInvestigation(validationData);

            if ('error' in result) {
                const errorMessage = getReadableError(result.error, 'Validation failed');

                // Show error on email field (most common duplicate issue)
                setErrors({ employeeEmail: errorMessage });
                setIsValidating(false);

                // Scroll to error
                const element = document.querySelector('[name="employeeEmail"]');
                element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                return;
            }

            // The validate response tells us whether this account must pay. Payment-exempt
            // (complimentary) employers skip checkout entirely.
            const validation = 'data' in result ? result.data : undefined;
            const paymentExempt = validation?.paymentExempt === true || validation?.requiresPayment === false;

            // Validation successful, proceed
            const details = {
                ...formData,
                employeePhone: phoneNumberWithCountryCode,
                paymentExempt,
            };

            setIsValidating(false);
            onNext(details);
        } catch (_err: any) {
            setIsValidating(false);
            setErrors({ employeeEmail: 'Validation failed. Please try again.' });
        }
    };

    const packageName = checkType === CheckType.STANDARD ? 'Standard' : 'Enhanced';

    return (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-end bg-black/50">
            <div className="w-full md:w-[600px] h-full md:h-screen bg-white md:rounded-l-3xl shadow-2xl overflow-y-auto">
                <div className="p-6 md:p-8">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">New Background Check</h2>
                            <p className="text-sm text-gray-600 mt-1 text-start">{packageName} Check</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
                        >
                            <X className="w-5 h-5 text-gray-600" />
                        </button>
                    </div>

                    {/* Personal Details Section */}
                    <div className="mb-8">
                        <h3 className="text-lg font-bold text-gray-900 mb-6 text-start">Employee Details</h3>

                        <div className="grid md:grid-cols-2 gap-6">
                            {/* One name field rather than three: it is how
                                people think of a name, and it matches the bulk
                                form and the spreadsheet import. Split into
                                first/middle/last on submit. */}
                            <div>
                                <label className="block text-sm font-medium text-gray-900 mb-2 text-start">
                                    Full Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="fullName"
                                    value={formData.fullName}
                                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                                    placeholder="John Adewale Doe"
                                    className={`w-full px-4 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent ${
                                        errors.fullName ? 'border-red-300 bg-red-50' : 'border-gray-200'
                                    }`}
                                />
                                {errors.fullName ? (
                                    <p className="text-xs text-red-600 mt-1">{errors.fullName}</p>
                                ) : (
                                    <p className="text-xs text-gray-400 mt-1 text-start">
                                        Any middle names go here too.
                                    </p>
                                )}
                            </div>

                            {/* Gender */}
                            <div>
                                <label className="block text-sm font-medium text-gray-900 mb-2 text-start">
                                    Gender <span className="text-gray-400">(Optional)</span>
                                </label>
                                <select
                                    value={formData.employeeGender}
                                    onChange={(e) => handleInputChange('employeeGender', e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                                >
                                    <option value="">Select gender</option>
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
                        </div>

                        {/* Email - Full Width */}
                        <div className="mt-6">
                            <label className="block text-sm font-medium text-gray-900 mb-2 text-start">
                                Email <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="email"
                                name="employeeEmail"
                                value={formData.employeeEmail}
                                onChange={(e) => handleInputChange('employeeEmail', e.target.value)}
                                placeholder="johndoe@gmail.com"
                                className={`w-full px-4 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent ${
                                    errors.employeeEmail ? 'border-red-300 bg-red-50' : 'border-gray-200'
                                }`}
                            />
                            {errors.employeeEmail && (
                                <p className="text-xs text-red-600 mt-1">{errors.employeeEmail}</p>
                            )}
                        </div>

                        {/* Phone Number - Full Width */}
                        <div className="mt-6">
                            <label className="block text-sm font-medium text-gray-900 mb-2 text-start">
                                Phone Number <span className="text-red-500">*</span>
                            </label>
                            <div className="flex gap-2">
                                <select
                                    value={formData.countryCode}
                                    onChange={(e) => handleInputChange('countryCode', e.target.value)}
                                    className="w-24 px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-transparent"
                                >
                                    <option value="+234">🇳🇬 +234</option>
                                    <option value="+1">🇺🇸 +1</option>
                                    <option value="+44">🇬🇧 +44</option>
                                    <option value="+91">🇮🇳 +91</option>
                                </select>
                                <input
                                    type="tel"
                                    name="employeePhone"
                                    value={formData.employeePhone}
                                    onChange={(e) => {
                                        // Only allow digits and limit to 10 digits (after removing leading zeros)
                                        let value = e.target.value.replace(/[^0-9]/g, '');

                                        // Remove leading zeros
                                        value = value.replace(/^0+/, '');

                                        // Limit to 10 digits
                                        if (value.length > 10) {
                                            value = value.slice(0, 10);
                                        }

                                        handleInputChange('employeePhone', value);
                                    }}
                                    placeholder="8012345678"
                                    maxLength={10}
                                    className={`flex-1 px-4 py-2.5 border ${
                                        errors.employeePhone ? 'border-red-300 bg-red-50' : 'border-gray-200'
                                    } rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-transparent`}
                                />
                            </div>
                            {errors.employeePhone && (
                                <p className="mt-1 text-xs text-red-600">{errors.employeePhone}</p>
                            )}
                        </div>
                    </div>

                    {/* Category Section — drives the medical test panel */}
                    <div className="mb-8">
                        <h3 className="text-lg font-bold text-gray-900 mb-2 text-start">Category</h3>
                        <p className="text-sm text-gray-600 mb-4 leading-relaxed text-start">
                            Determines the medical test panel applied to this check.
                        </p>
                        <select
                            value={formData.investigationCategory}
                            onChange={(e) => handleInputChange('investigationCategory', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        >
                            <option value={InvestigationCategory.EMPLOYMENT}>Employment</option>
                            <option value={InvestigationCategory.DOMESTIC_STAFF}>Domestic Staff</option>
                        </select>
                    </div>

                    {/* Upload Documents Section */}
                    <div className="mb-8">
                        <h3 className="text-lg font-bold text-gray-900 mb-2 text-start">
                            Upload Documents <span className="text-gray-400">(Optional)</span>
                        </h3>
                        <p className="text-sm text-gray-600 mb-6 leading-relaxed text-start">
                            You can optionally upload relevant documents now, or the employee can upload them later via
                            their invitation link.
                        </p>

                        {/* Document Type Selector */}
                        <div className="space-y-4">
                            {(
                                [
                                    { value: 'cv', label: 'CV/Resume' },
                                    { value: 'national_id', label: 'National ID' },
                                    { value: 'nin', label: 'NIN' },
                                    { value: 'other', label: 'Other' },
                                ] as const
                            ).map(({ value: docType, label }) => (
                                <div key={docType} className="border border-gray-200 rounded-xl p-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="text-sm font-medium text-gray-900">{label}</label>
                                        <label className="cursor-pointer">
                                            <span className="text-sm text-[#FF6B35] font-medium hover:underline">
                                                + Add File
                                            </span>
                                            <input
                                                type="file"
                                                onChange={(e) => handleFileUpload(e, docType)}
                                                className="hidden"
                                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                                multiple
                                            />
                                        </label>
                                    </div>

                                    {/* Show uploaded files for this type */}
                                    {uploadedFiles.filter((f) => f.type === docType).length > 0 && (
                                        <div className="space-y-2 mt-3">
                                            {uploadedFiles
                                                .filter((f) => f.type === docType)
                                                .map((uploadedFile) => (
                                                    <div
                                                        key={uploadedFile.id}
                                                        className="flex items-center justify-between bg-gray-50 p-2 rounded-lg"
                                                    >
                                                        <span className="text-xs text-gray-700 truncate flex-1">
                                                            {uploadedFile.file.name}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveFile(uploadedFile.id)}
                                                            className="ml-2 w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300"
                                                        >
                                                            <X className="w-3 h-3 text-gray-600" />
                                                        </button>
                                                    </div>
                                                ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {uploadedFiles.length > 0 && (
                            <p
                                className={`text-xs mt-4 ${
                                    uploadedFiles.length > 10 ? 'text-red-600' : 'text-gray-500'
                                }`}
                            >
                                {uploadedFiles.length} file(s) ready to upload (max 10 files)
                            </p>
                        )}
                        {errors.documents && <p className="text-xs text-red-600 mt-2">{errors.documents}</p>}
                    </div>

                    {externalError && (
                        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4">
                            <p className="text-sm text-red-600">{externalError}</p>
                        </div>
                    )}

                    {/* Next Button */}
                    <Button
                        onClick={handleNext}
                        disabled={isValidating || externalLoading}
                        className="w-full bg-gray-900 hover:bg-gray-800 text-white py-6 rounded-xl text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isValidating ? (
                            <span className="flex items-center justify-center gap-2">
                                <Loader2 className="w-5 h-5 animate-spin" />
                                Validating...
                            </span>
                        ) : externalLoading ? (
                            <span className="flex items-center justify-center gap-2">
                                <Loader2 className="w-5 h-5 animate-spin" />
                                Creating investigation...
                            </span>
                        ) : (
                            'Next: Payment'
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
}
